/**
 * Pivot observations météo horaires (Date × Heure) pour check-claims séjour.
 * Source typique : GET /api/trips/check-claims?reservation_number=&policy_code=
 */

const pad2 = (value) => String(value).padStart(2, '0');

export const normalizeHourLabel = (raw) => {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        const h = Math.trunc(raw);
        return `${pad2(h)}:00`;
    }
    const text = String(raw).trim();
    const hhmmss = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (hhmmss) return `${pad2(hhmmss[1])}:${hhmmss[2]}`;
    const onlyHour = text.match(/^(\d{1,2})h?$/i);
    if (onlyHour) return `${pad2(onlyHour[1])}:00`;
    return text;
};

export const normalizeDateLabel = (raw) => {
    if (raw == null || raw === '') return null;
    const text = String(raw).trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return text;
    const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
    const dmy = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (dmy) return `${pad2(dmy[1])}/${pad2(dmy[2])}/${dmy[3]}`;
    return text;
};

const dateFromParts = (day, month, year) => {
    if (day == null || month == null || year == null) return null;
    return `${pad2(day)}/${pad2(month)}/${year}`;
};

const dateSortKey = (label) => {
    const m = String(label).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return label;
    return `${m[3]}-${m[2]}-${m[1]}`;
};

const hourSortKey = (label) => {
    const m = String(label).match(/^(\d{1,2})/);
    return m ? Number(m[1]) : 0;
};

/** Affiche les valeurs météo avec 1 décimale (ex. 0.0, 21.3). */
export const toDisplayValue = (value) => {
    if (value == null || value === '') return '—';
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value.toFixed(1);
    }
    const text = String(value).trim().replace(',', '.');
    const n = Number(text);
    if (!Number.isNaN(n) && text !== '') {
        return n.toFixed(1);
    }
    return String(value);
};

const pushPoint = (list, date, hour, value) => {
    const d = normalizeDateLabel(date);
    const h = normalizeHourLabel(hour);
    if (!d || !h) return;
    if (value === null || value === undefined || value === '') return;
    list.push({ date: d, hour: h, value });
};

const tripDayFromClaim = (item) => (
    item?.forecast_info?.trip_day
    || item?.last_observation_info?.trip_day
    || item?.activity_info?.activity_start_date
    || dateFromParts(
        item?.last_observation_info?.last_observation_init_day,
        item?.last_observation_info?.last_observation_init_month,
        item?.last_observation_info?.last_observation_init_year
    )
);

/**
 * Parse "2025-11-16 10:00:00" / "2025-11-16T10:00:00" / "16/11/2025 10:00".
 * @returns {{ date: string|null, hour: string|null }}
 */
export const parseObservationDateTime = (raw) => {
    if (raw == null || raw === '') return { date: null, hour: null };
    const text = String(raw).trim();
    const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::\d{2})?/);
    if (iso) {
        return {
            date: `${iso[3]}/${iso[2]}/${iso[1]}`,
            hour: `${pad2(iso[4])}:${iso[5]}`,
        };
    }
    const dmy = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})[ T](\d{1,2}):(\d{2})(?::\d{2})?/);
    if (dmy) {
        return {
            date: `${pad2(dmy[1])}/${pad2(dmy[2])}/${dmy[3]}`,
            hour: `${pad2(dmy[4])}:${dmy[5]}`,
        };
    }
    return { date: null, hour: null };
};

/**
 * Ligne horaire : { date|jour, heure|time|hour, precipitation|prcp, temperature|temp }
 * Format claims fréquent : { prcp: "0,0", time: "2025-11-16 10:00:00" }
 */
const extractFromObservationArray = (obsList, tripDay, precipPoints, tempPoints) => {
    if (!Array.isArray(obsList)) return;
    obsList.forEach((obs) => {
        if (!obs || typeof obs !== 'object') return;

        let date = obs.date || obs.jour || obs.trip_day || tripDay;
        if (!date && obs.forecast_day != null) {
            date = dateFromParts(obs.forecast_day, obs.forecast_month, obs.forecast_year);
        }

        let hour = obs.heure ?? obs.hour ?? obs.forecast_hour ?? null;
        if (obs.time != null && obs.time !== '') {
            const parsed = parseObservationDateTime(obs.time);
            if (parsed.date || parsed.hour) {
                date = date || parsed.date;
                hour = hour ?? parsed.hour;
            } else if (hour == null) {
                hour = obs.time;
            }
        }

        const precip = obs.precipitation ?? obs.prcp ?? obs.prc ?? obs.value_precip
            ?? (obs.value != null && obs.temperature == null && obs.temp == null ? obs.value : undefined);
        const temp = obs.temperature ?? obs.temp ?? obs.tmp ?? obs.value_temp;

        pushPoint(precipPoints, date, hour, precip);
        pushPoint(tempPoints, date, hour, temp);

        // Format forecast classique { forecast_day, forecast_hour, value }
        if (obs.forecast_day != null && obs.value != null && precip == null && temp == null) {
            const d = dateFromParts(obs.forecast_day, obs.forecast_month, obs.forecast_year);
            pushPoint(precipPoints, d, obs.forecast_hour, obs.value);
        }
    });
};

const extractFromWeatherSeries = (series, points, preferKey) => {
    if (!Array.isArray(series)) return;
    series.forEach((row) => {
        if (!row || typeof row !== 'object') return;
        const date = row.date
            || row.jour
            || dateFromParts(row.forecast_day, row.forecast_month, row.forecast_year);
        const hour = row.heure ?? row.time ?? row.hour ?? row.forecast_hour;
        let value;
        if (preferKey === 'precipitation') {
            value = row.precipitation ?? row.prcp ?? row.prc ?? row.value;
        } else if (preferKey === 'temperature') {
            value = row.temperature ?? row.temp ?? row.tmp ?? row.value;
        } else {
            value = row.precipitation ?? row.temperature ?? row.prcp ?? row.temp ?? row.value;
        }
        pushPoint(points, date, hour, value);
    });
};

const OBSERVATION_ARRAY_PATHS = [
    ['last_observation_info', 'last_observation_data'],
    ['meteomatics_observations_info', 'meteomatics_observation_data'],
    ['meteofrance_observations_info', 'meteofrance_observation_data'],
    ['meteostat_observations_info', 'meteostat_observation_data'],
    ['observations'],
    ['hourly_observations'],
    ['weather_observations'],
    ['observation_data'],
];

const getNestedArray = (obj, path) => {
    const value = path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
    return Array.isArray(value) ? value : null;
};

/**
 * Extrait les points précipitation / température depuis la liste check_claims
 * (et éventuelles listes horaires à la racine du payload).
 */
export const extractStayWeatherPoints = (checkClaims, rootPayload = null) => {
    const precipPoints = [];
    const tempPoints = [];
    const list = Array.isArray(checkClaims) ? checkClaims : [];

    list.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const tripDay = tripDayFromClaim(item);

        OBSERVATION_ARRAY_PATHS.forEach((path) => {
            const arr = getNestedArray(item, path);
            if (arr) extractFromObservationArray(arr, tripDay, precipPoints, tempPoints);
        });

        extractFromWeatherSeries(item?.forecast_info?.weather_data, precipPoints, 'precipitation');
        extractFromWeatherSeries(item?.forecast_info?.weather_temp, tempPoints, 'temperature');
        extractFromWeatherSeries(item?.weather_data, precipPoints, 'precipitation');
        extractFromWeatherSeries(item?.weather_temp, tempPoints, 'temperature');

        // Ligne plate éventuelle { date, heure, precipitation, temperature }
        if (
            item.date != null
            || item.heure != null
            || item.hour != null
            || item.precipitation != null
            || item.temperature != null
        ) {
            pushPoint(precipPoints, item.date, item.heure ?? item.hour ?? item.time, item.precipitation);
            pushPoint(tempPoints, item.date, item.heure ?? item.hour ?? item.time, item.temperature);
        }
    });

    // Listes horaires éventuelles à la racine de la réponse API
    if (rootPayload && typeof rootPayload === 'object' && !Array.isArray(rootPayload)) {
        ['observations', 'hourly_observations', 'weather_observations', 'observation_data'].forEach((key) => {
            if (Array.isArray(rootPayload[key])) {
                extractFromObservationArray(rootPayload[key], null, precipPoints, tempPoints);
            }
        });
    }

    return { precipPoints, tempPoints };
};

/** Parse une valeur numérique (null si absente / invalide). */
export const toNumericValue = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const text = String(value).trim().replace(',', '.');
    const n = Number(text);
    return Number.isNaN(n) || text === '' ? null : n;
};

export const formatStatNumber = (value, digits = 1) => {
    if (value == null || Number.isNaN(value)) return '—';
    return Number(value).toFixed(digits);
};

/** Intensité 0–1 pour heatmap. */
export const getHeatIntensity = (value, min, max) => {
    if (value == null || min == null || max == null) return null;
    if (max === min) return 0.35;
    return Math.max(0, Math.min(1, (value - min) / (max - min)));
};

/** Bleu clair → bleu foncé (précipitations). */
export const precipHeatColor = (intensity) => {
    if (intensity == null) return 'transparent';
    const t = intensity;
    const r = Math.round(239 - t * (239 - 37));
    const g = Math.round(246 - t * (246 - 99));
    const b = Math.round(255 - t * (255 - 235));
    return `rgb(${r}, ${g}, ${b})`;
};

/** Bleu → vert → orange → rouge (températures). */
export const tempHeatColor = (intensity) => {
    if (intensity == null) return 'transparent';
    const stops = [
        [59, 130, 246],
        [34, 197, 94],
        [249, 115, 22],
        [239, 68, 68],
    ];
    const scaled = intensity * (stops.length - 1);
    const i = Math.floor(scaled);
    const f = scaled - i;
    const a = stops[Math.min(i, stops.length - 1)];
    const b = stops[Math.min(i + 1, stops.length - 1)];
    const r = Math.round(a[0] + (b[0] - a[0]) * f);
    const g = Math.round(a[1] + (b[1] - a[1]) * f);
    const bl = Math.round(a[2] + (b[2] - a[2]) * f);
    return `rgb(${r}, ${g}, ${bl})`;
};

export const buildPivot = (points) => {
    const dateSet = new Set();
    const hourSet = new Set();
    const cellMap = new Map();
    const numericValues = [];

    points.forEach(({ date, hour, value }) => {
        dateSet.add(date);
        hourSet.add(hour);
        cellMap.set(`${date}|${hour}`, value);
        const n = toNumericValue(value);
        if (n != null) numericValues.push(n);
    });

    const dates = Array.from(dateSet).sort((a, b) => dateSortKey(a).localeCompare(dateSortKey(b)));
    const hours = Array.from(hourSet).sort((a, b) => hourSortKey(a) - hourSortKey(b));

    const rows = hours.map((hour) => ({
        hour,
        cells: dates.map((date) => {
            const raw = cellMap.get(`${date}|${hour}`);
            const num = toNumericValue(raw);
            return {
                display: raw == null || raw === '' ? '—' : toDisplayValue(raw),
                value: num,
                date,
                hour,
            };
        }),
    }));

    return {
        dates,
        hours,
        rows,
        count: numericValues.length,
        min: numericValues.length ? Math.min(...numericValues) : null,
        max: numericValues.length ? Math.max(...numericValues) : null,
    };
};

const computeWeatherStats = (precipPivot, tempPivot) => {
    const precipValues = [];
    (precipPivot?.rows || []).forEach((row) => {
        row.cells.forEach((cell) => {
            if (cell.value != null) precipValues.push(cell.value);
        });
    });
    const tempValues = [];
    (tempPivot?.rows || []).forEach((row) => {
        row.cells.forEach((cell) => {
            if (cell.value != null) tempValues.push(cell.value);
        });
    });

    const dates = Array.from(new Set([
        ...(precipPivot?.dates || []),
        ...(tempPivot?.dates || []),
    ])).sort((a, b) => dateSortKey(a).localeCompare(dateSortKey(b)));

    const observationPeriod = dates.length === 0
        ? '—'
        : dates.length === 1
            ? dates[0]
            : `${dates[0]} → ${dates[dates.length - 1]}`;

    return {
        totalRainfall: precipValues.reduce((s, v) => s + v, 0),
        maxRainfall: precipValues.length ? Math.max(...precipValues) : null,
        avgTemperature: tempValues.length
            ? tempValues.reduce((s, v) => s + v, 0) / tempValues.length
            : null,
        minTemperature: tempValues.length ? Math.min(...tempValues) : null,
        maxTemperature: tempValues.length ? Math.max(...tempValues) : null,
        observationPeriod,
    };
};

const buildClientSummary = (checkClaims) => {
    const item = Array.isArray(checkClaims) ? checkClaims[0] : null;
    if (!item) return null;
    const client = item.client_info || {};
    const activity = item.activity_info || {};
    const policy = item.policy_info || {};
    const name = [client.first_name, client.last_name].filter(Boolean).join(' ').trim();
    const destination = [
        activity.activity_city || activity.city,
        activity.activity_country || activity.country,
    ].filter(Boolean).join(', ');
    const start = activity.activity_start_date || activity.start_date;
    const end = activity.activity_end_date || activity.end_date;
    return {
        fullName: name || '—',
        email: client.email || '—',
        phone: client.phone_number || client.phone || '—',
        clientCode: client.client_code || '—',
        reservationNumber: activity.reservation_number || '—',
        policyCode: policy.policy_code || '—',
        destination: destination || '—',
        travelDates: start && end ? `${start} → ${end}` : (start || end || '—'),
        claimStatus: item.claim_status || item.status || policy.claim_status || '—',
        coverage: policy.coverage_name || policy.product_name || policy.policy_type || '—',
    };
};

/**
 * Enrichit un claim avec les infos héritées du bloc police (réponse api/trips/claims).
 */
const mergeClaimWithBlock = (claim, block) => {
    if (!claim || typeof claim !== 'object') return claim;
    if (!block || typeof block !== 'object') return claim;
    return {
        ...claim,
        client_info: claim.client_info || block.client_info || block.policy_info?.client_info,
        activity_info: claim.activity_info || block.activity_info || block.policy_info?.activity_info,
        policy_info: claim.policy_info || block.policy_info,
        partner_info: claim.partner_info || block.partner_info || block.policy_info?.partner_info,
    };
};

/**
 * @param {object|array} payload — réponse GET /api/trips/check-claims ou /api/trips/claims
 * @returns {object[]} liste plate d'items « claim-like » (observations / client / activité)
 */
export const getCheckClaimsListFromPayload = (payload) => {
    const candidates = [
        payload?.policies_with_claims,
        payload?.data?.policies_with_claims,
        payload?.check_claims,
        payload?.claims,
        payload?.data?.check_claims,
        payload?.data?.claims,
        payload?.policies,
        payload?.data?.policies,
        payload?.data,
        payload?.results,
        Array.isArray(payload) ? payload : null,
    ];
    const list = candidates.find((entry) => Array.isArray(entry)) || [];

    const flattened = [];
    list.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        if (Array.isArray(item.claims) && item.claims.length > 0) {
            item.claims.forEach((claim) => {
                if (claim && typeof claim === 'object') {
                    flattened.push(mergeClaimWithBlock(claim, item));
                }
            });
            return;
        }
        // Bloc police sans claims mais avec observations à la racine
        flattened.push(item);
    });
    return flattened;
};

/**
 * Construit les deux tableaux pivot (précipitations + température) + stats / résumé.
 * @param {object|array} payload
 */
export const buildStayWeatherPivotTables = (payload) => {
    const checkClaims = getCheckClaimsListFromPayload(payload);
    const { precipPoints, tempPoints } = extractStayWeatherPoints(checkClaims, payload);
    const precipitation = buildPivot(precipPoints);
    const temperature = buildPivot(tempPoints);
    return {
        checkClaimsCount: checkClaims.length,
        precipitation,
        temperature,
        stats: computeWeatherStats(precipitation, temperature),
        summary: buildClientSummary(checkClaims),
    };
};
